<template>
  <!-- Row = 46px (name 24 + gap 2 + language 20), gap 16px: 5 rows = 294px, the rest scrolls -->
  <ul
    class="flex flex-col gap-4 max-h-[294px] overflow-y-auto py-1 -my-1 pl-1 -ml-1 pr-2 scrollbar scrollbar-thin scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-thumb-[#f0f6ff57] scrollbar-track-transparent"
    data-testid="participants-list"
  >
    <li
      v-for="participant in participants"
      :key="participant.id"
      class="flex items-center gap-4"
      :data-testid="`participants-list-item-${participant.id}`"
    >
      <span
        class="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-white/10"
        :class="{ 'outline outline-2 outline-offset-2 outline-amber-400': participant.raisedHand }"
      >
        <img
          :src="`/data/flags/${participant.language}.png`"
          :alt="participant.language"
          class="w-full h-full object-cover"
        />
      </span>
      <span class="flex flex-col gap-0.5 min-w-0">
        <span class="text-white text-base font-semibold truncate">{{ participant.name }}</span>
        <span class="text-text-neutral-medium text-sm truncate">{{ languageName(participant.language) }}</span>
      </span>
      <span
        v-if="participant.raisedHand"
        class="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-1 text-[14px] font-medium uppercase tracking-[0.08em] text-amber-100"
      >
        <span class="relative inline-flex items-center justify-center">
          <span class="absolute right-[15px] inline-flex min-w-[1rem] h-4 items-center justify-center rounded-full bg-transparent px-1 text-[9px] font-bold text-amber-100 leading-none shadow-none">
            {{ participant.raisedHandOrder || 1 }}
          </span>
          ✋
        </span>
      </span>

      <span
        v-if="participant.thumbsUp"
        class="ml-2 inline-flex items-center justify-center text-[16px] text-emerald-100 thumbs-up-badge"
      >
        👍
      </span>

      <span
        v-if="participant.laugh"
        class="ml-2 inline-flex items-center justify-center text-[16px] text-yellow-100 laughter-badge"
      >
        😂
      </span>

      <span
        v-if="participant.heart"
        class="ml-2 inline-flex items-center justify-center text-[16px] text-rose-100 heart-badge"
      >
        ❤️
      </span>

      <span
        v-if="participant.clap"
        class="ml-2 inline-flex items-center justify-center text-[16px] text-violet-100 clap-badge"
      >
        👏
      </span>
    </li>
  </ul>
</template>

<style scoped>
.thumbs-up-badge,
.laughter-badge,
.heart-badge,
.clap-badge {
  animation: thumbs-up-fade 2s ease-in-out forwards;
}

@keyframes thumbs-up-fade {
  0% {
    opacity: 0;
    transform: translateY(4px) scale(0.85);
  }
  15% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  75% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-4px) scale(1.1);
  }
}
</style>

<script>
import { mapState } from 'vuex';

export default {
  name: 'ParticipantsList',
  props: {
    participants: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapState('lang', ['languages']),
  },
  methods: {
    // Returns { lang, isDialect } for a dialect code like "en-US",
    // or a root language when it has no dialects to pick from.
    findLanguage(code) {
      for (const lang of this.languages) {
        if (lang.code === code && (!lang.dialects || lang.dialects.length <= 1)) {
          return { lang, isDialect: false };
        }
        const dialect = (lang.dialects || []).find(d => d.code === code);
        if (dialect) {
          return { lang: dialect, isDialect: true };
        }
      }
      return null;
    },
    // Same resolution as LanguagePicker: i18n override by key prefix
    // ("dialect_<code>" / "language_<code>"), otherwise the API name.
    languageName(code) {
      const found = this.findLanguage(code);
      if (!found) {
        return code;
      }
      const { lang, isDialect } = found;
      const activeLocale = this.$i18n.locale;
      const translations = this.$i18n.messages?.[activeLocale];
      const prefix = isDialect ? `dialect_${code}` : `language_${code}`;
      const matchingKey = translations
        ? Object.keys(translations).find(key => key.startsWith(prefix))
        : null;
      return (matchingKey ? this.$t(matchingKey) : (lang.native_name || lang.name)).trim();
    }
  }
}
</script>
